import { lazy, Suspense, type ComponentType, type ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router";
import { EmptyState, Loading } from "@otii/ui/ui";
import { Shell } from "./components/Shell";
import { AuthGate } from "./features/auth/AuthGate";
import { SignIn, SignInLink } from "./features/auth/SignIn";
import { useMe } from "./lib/queries";

function page<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() => load().then((m) => ({ default: m[name] })));
}

const FinancePage = page(() => import("./features/finance/FinancePage"), "FinancePage");
const SnapshotPage = page(() => import("./features/finance/SnapshotPage"), "SnapshotPage");
const AdminPage = page(() => import("./features/admin/AdminPage"), "AdminPage");
const HomePage = page(() => import("./features/home/HomePage"), "HomePage");
const DecisionsPage = page(() => import("./features/decisions/DecisionsPage"), "DecisionsPage");
const RisksPage = page(() => import("./features/risks/RisksPage"), "RisksPage");
const RiskIssuePage = page(() => import("./features/risks/IssuePage"), "IssuePage");
const ObligationsPage = page(() => import("./features/obligations/ObligationsPage"), "ObligationsPage");
const ReleasesPage = page(() => import("./features/releases/ReleasesPage"), "ReleasesPage");
const MeasuresPage = page(() => import("./features/measures/MeasuresPage"), "MeasuresPage");
const PackPage = page(() => import("./features/pack/PackPage"), "PackPage");
const BoardPage = page(() => import("./features/pack/PackPage"), "BoardPage");
const CalendarPage = page(() => import("./features/calendar/CalendarPage"), "CalendarPage");
const RecordsPage = page(() => import("./features/records/RecordsPage"), "RecordsPage");
const IssuedPackPage = page(() => import("./features/records/RecordsPage"), "IssuedPackPage");

function Lazy({ el }: { el: ReactElement }) {
  return <Suspense fallback={<Loading />}>{el}</Suspense>;
}

/** Observers land on Records (issued packs); everyone else on Home. */
function Landing() {
  const { data: me } = useMe();
  if (!me) return null;
  return <Navigate to={me.user.role === "observer" ? "/records" : "/home"} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/sign-in/link" element={<SignInLink />} />
      <Route
        element={
          <AuthGate>
            <Shell />
          </AuthGate>
        }
      >
        <Route index element={<Landing />} />
        <Route path="home" element={<Lazy el={<HomePage />} />} />
        <Route path="decisions/:tab?" element={<Lazy el={<DecisionsPage />} />} />
        {/* Maggie's screen id */}
        <Route path="actions" element={<Navigate to="/decisions/actions" replace />} />
        <Route path="risks/issues/:id" element={<Lazy el={<RiskIssuePage />} />} />
        <Route path="risks/:tab?" element={<Lazy el={<RisksPage />} />} />
        <Route path="risk" element={<Navigate to="/risks" replace />} />
        <Route path="obligations" element={<Lazy el={<ObligationsPage />} />} />
        <Route path="obl" element={<Navigate to="/obligations" replace />} />
        <Route path="releases" element={<Lazy el={<ReleasesPage />} />} />
        <Route path="release" element={<Navigate to="/releases" replace />} />
        <Route path="measures/:tab?" element={<Lazy el={<MeasuresPage />} />} />
        <Route path="pack" element={<Lazy el={<PackPage />} />} />
        <Route path="board" element={<Lazy el={<BoardPage />} />} />
        <Route path="calendar" element={<Lazy el={<CalendarPage />} />} />
        {/* Maggie's screen id */}
        <Route path="cal" element={<Navigate to="/calendar" replace />} />
        <Route path="records/packs/:id" element={<Lazy el={<IssuedPackPage />} />} />
        <Route path="records" element={<Lazy el={<RecordsPage />} />} />
        <Route path="records/minutes" element={<Lazy el={<RecordsPage />} />} />
        <Route path="records/drive" element={<Lazy el={<RecordsPage />} />} />
        <Route path="records/history" element={<Lazy el={<RecordsPage />} />} />
        {/* Maggie's screen id */}
        <Route path="archive" element={<Navigate to="/records" replace />} />
        <Route path="finance/snapshots/:id" element={<Lazy el={<SnapshotPage />} />} />
        <Route path="finance/:tab?" element={<Lazy el={<FinancePage />} />} />
        <Route path="admin/*" element={<Lazy el={<AdminPage />} />} />
        <Route path="*" element={<EmptyState title="Page not found" />} />
      </Route>
    </Routes>
  );
}
